/**
 * DebtPriorityList — "Where extra payments help most".
 *
 * Ranked by the backend (avalanche default, snowball also returned), and every
 * row carries the REAL months/interest a bit more money would save on that debt,
 * computed server-side. The toggle changes the ordering, not the maths.
 */
import React, { useState } from 'react';
import { TrendingDown } from 'lucide-react';
import { formatMoney } from '@/lib/format';

const STRATEGY_NOTE = {
  avalanche: 'Highest rate first — the cheapest way to spend a spare amount.',
  snowball: 'Smallest balance first — the fastest visible win.',
};

export function DebtPriorityList({ priorities }) {
  const [strategy, setStrategy] = useState(priorities?.default || 'avalanche');
  const rows = priorities?.[strategy] || [];
  if (rows.length === 0) return null;

  return (
    <div className="bg-sage-light/40 rounded-2xl border border-sage/40 p-5 shadow-sm mb-4"
         data-testid="debt-priority-card">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="font-display text-base md:text-lg text-charcoal leading-tight mb-1">
            Where extra payments help most
          </p>
          <p className="text-xs text-muted-foreground">{STRATEGY_NOTE[strategy]}</p>
        </div>
        <div className="inline-flex rounded-full border border-sage/50 bg-card/70 p-0.5">
          {['avalanche', 'snowball'].map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setStrategy(s)}
              className={`text-xs px-3 py-1 rounded-full capitalize transition-colors ${
                strategy === s ? 'bg-evergreen text-white' : 'text-muted-foreground hover:text-charcoal'
              }`}
              data-testid={`debt-priority-strategy-${s}`}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      <ul className="mt-4 divide-y divide-sage/30">
        {rows.map((r) => {
          const impact = r.extra_impact || {};
          return (
            <li key={r.debt_id} className="py-3 flex flex-wrap items-baseline justify-between gap-3"
                data-testid={`debt-priority-row-${r.debt_id}`}>
              <div className="min-w-0">
                <p className="text-sm font-medium text-charcoal truncate">
                  <span className="text-muted-foreground tabular-nums mr-1.5">{r.rank}.</span>
                  {r.name}
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {formatMoney(r.balance, r.currency, { compact: true })}
                  {r.apr ? ` · ${r.apr}%` : ' · no rate on record'}
                  {r.payment ? ` · pays ${formatMoney(r.payment, r.currency)}/mo` : ''}
                </p>
              </div>
              {impact.months ? (
                <p className="text-xs text-charcoal max-w-[16rem] text-right"
                   data-testid={`debt-priority-impact-${r.debt_id}`}>
                  <TrendingDown className="inline h-3.5 w-3.5 mb-0.5 mr-1 text-evergreen" aria-hidden="true" />
                  {formatMoney(impact.extra_monthly, r.currency)}/mo more here
                  {impact.months_saved ? ` clears it ${impact.months_saved} month${impact.months_saved === 1 ? '' : 's'} sooner` : ' barely moves it'}
                  {impact.interest_saved ? (
                    <span className="text-muted-foreground">
                      {' '}and saves {formatMoney(impact.interest_saved, r.currency, { compact: true })} in interest
                    </span>
                  ) : null}
                </p>
              ) : null}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export default DebtPriorityList;
